"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon, Clock, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { TimePickerDemo } from "@/components/forms/time-picker"
import { getDateLocale } from "@/lib/date-utils"
import { cn } from "@/lib/utils"
import {
  useCalendarTranslations,
  useCommonTranslations,
  useComponentsTranslations,
} from "@/hooks/use-translations"
import { useLocale } from "@/lib/i18n-client"

type TaskDeadlineFieldProps = {
  id?: string
  presentation?: "anchored" | "dialog"
  panelSide?: "top" | "bottom"
  value: string
  onChange: (value: string) => void
}

function formatLocalDateTimeValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

function parseLocalDateTimeValue(value: string): Date | undefined {
  if (!value) return undefined

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return undefined

  return parsed
}

export function TaskDeadlineField({
  id = "deadline",
  presentation = "anchored",
  panelSide = "bottom",
  value,
  onChange,
}: TaskDeadlineFieldProps) {
  const tComponents = useComponentsTranslations()
  const tCalendar = useCalendarTranslations()
  const tCommon = useCommonTranslations()
  const locale = useLocale()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(parseLocalDateTimeValue(value))
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(parseLocalDateTimeValue(value))

  useEffect(() => {
    const parsed = parseLocalDateTimeValue(value)
    setSelectedDate(parsed)
    setSelectedTime(parsed)
  }, [value])

  const selectedDateTime = useMemo(() => {
    if (!selectedDate) return undefined

    const merged = new Date(selectedDate)
    const timeSource = selectedTime ?? selectedDate
    merged.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0)
    return merged
  }, [selectedDate, selectedTime])

  const commitDateTime = useCallback((nextDate: Date | undefined, nextTime: Date | undefined) => {
    if (!nextDate) {
      onChange("")
      return
    }

    const merged = new Date(nextDate)
    const timeSource = nextTime ?? nextDate
    merged.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0)
    onChange(formatLocalDateTimeValue(merged))
  }, [onChange])

  const handleDateSelect = useCallback((nextDate: Date | undefined) => {
    setSelectedDate(nextDate)
    commitDateTime(nextDate, selectedTime)
  }, [commitDateTime, selectedTime])

  const handleTimeChange = useCallback((nextTime: Date | undefined) => {
    setSelectedTime(nextTime)
    commitDateTime(selectedDate, nextTime)
  }, [commitDateTime, selectedDate])

  const handleClear = useCallback(() => {
    setSelectedDate(undefined)
    setSelectedTime(undefined)
    onChange("")
    setIsOpen(false)
  }, [onChange])

  const pickerContent = (
    <div className="space-y-4">
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        locale={getDateLocale(locale)}
        initialFocus
      />

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" />
          <span>{tComponents("end_time")}</span>
        </div>
        <TimePickerDemo
          date={selectedTime ?? selectedDate ?? new Date()}
          setDate={handleTimeChange}
        />
      </div>

      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <Button variant="ghost" size="sm" onClick={handleClear} type="button">
          {tCommon("none")}
        </Button>
        <Button size="sm" onClick={() => setIsOpen(false)} type="button">
          {tCommon("save")}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{tComponents("deadline")}</Label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          {isOpen && (
            presentation === "dialog" ? (
              <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="w-fit max-w-[calc(100vw-2rem)] p-4 sm:max-w-fit">
                  <DialogHeader className="sr-only">
                    <DialogTitle>{tComponents("deadline")}</DialogTitle>
                    <DialogDescription>{tCalendar("select_date")}</DialogDescription>
                  </DialogHeader>
                  {pickerContent}
                </DialogContent>
              </Dialog>
            ) : (
              <div
                className="fixed inset-0 z-[90] bg-background/40 backdrop-blur-[2px]"
                onClick={() => setIsOpen(false)}
              />
            )
          )}

          <div className="relative">
            <Button
              id={id}
              type="button"
              variant="outline"
              className="w-full justify-start text-left font-normal"
              onClick={() => setIsOpen((open) => !open)}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDateTime
                ? format(selectedDateTime, "PPP HH:mm", { locale: getDateLocale(locale) })
                : tCalendar("select_date")}
            </Button>

            {isOpen && presentation === "anchored" && (
              <div
                className={cn(
                  "absolute left-0 z-[100]",
                  panelSide === "top" ? "bottom-full mb-2" : "top-full mt-2",
                )}
              >
                <div className="w-auto rounded-lg border bg-popover p-3 text-popover-foreground shadow-xl">
                  {pickerContent}
                </div>
              </div>
            )}
          </div>
        </div>

        {value && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleClear}
            title={tCommon("none")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
